<template>
  <v-container>
    <v-row no-gutters>
      <v-col cols="12">
        <h2 class="my-4">Appoint the Executor of your estate</h2>
        <h3>What is an Executor?</h3>
        <br />
        <ul class="mb-2">
          <li>
            An Executor is the company (or person) which will attend to the
            administration of your deceased estate when you pass on.
          </li>
          <li>
            They will need to make a list of your assets and liabilities, ensure
            that your assets are distributed in accordance with the terms of
            your will and manage your estate until such time as the distribution
            of your assets have been finalised.
          </li>
        </ul>
        <div v-if="!omOnlyExecutor">
          <div v-if="this.whitelabel.brand === 'quickwill'">
            <h3 class="mt-6">Who should I choose as my Executor?</h3>
            <br />
            <ul>
              <li>
                Unless you have a specific, capable person who you trust and who
                you believe will be up to the time consuming and often
                challenging task, then we suggest appointing QuickWill’s as
                Executor.
              </li>
              <li>
                QuickWill administers estates through Online Wills Estate
                Administrators.
              </li>
              <li>
                If you choose a family member or friend, they can:
                <ul>
                  <li>
                    accept and get a professional to assist them with the work
                  </li>
                  <li>
                    decline, and an alternative Executor will be appointed
                  </li>
                </ul>
              </li>
              <li>Your Executor must reside in {{ country }}</li>
              <li v-if="isZAWill">
                Your Executor can legally charge a fee of 3.5% (+ VAT) of the
                value of your estate to perform their duties, plus 6% (+VAT) of
                all income generated through your estate during the
                administration thereof. These fees can be negotiated.
              </li>
            </ul>
          </div>
          <div v-else>
            <h3 class="mt-6">Who should I choose as my Executor?</h3>
            <br />
            <ul>
              <li>
                Unless you have a specific, capable person who you trust and who
                you believe will be up to the time consuming and often
                challenging task, then we suggest using {{ companyNameText }}.
              </li>
              <li>
                If you choose a family member or friend, they can:
                <ul>
                  <li>
                    accept and get a professional to assist them with the work
                  </li>
                  <li>
                    decline, and an alternative Executor will be appointed
                  </li>
                </ul>
              </li>
              <li>Your Executor must reside in {{ country }}</li>
              <li v-if="isZAWill">
                Your Executor can legally charge a fee of 3.5% (+ VAT) of the
                value of your estate to perform their duties, plus 6% (+VAT) of
                all income generated through your estate during the
                administration thereof. These fees can be negotiated.
              </li>
            </ul>
          </div>
        </div>
        <div v-else>
          <h3 class="mt-6">Who is my Executor?</h3>
          <br />
          <ul>
            <li>Old Mutual Will will be your Executor.</li>
            <li>
              Your Executor can legally charge a fee of 3.5% (+ VAT) of the
              values of your estate to perform their duties, plus 6% (+ VAT) of
              all income generated through your estate during the administration
              thereof.
            </li>
          </ul>
        </div>
        <div v-if="showLearnMoreAboutQWExecutorLink" class="mt-5">
          <router-link to="/executor-explainer" target="_blank">
            Learn more about QuickWill as Executor
          </router-link>
        </div>
        <br />
        <ValidationObserver ref="observer" v-if="!omOnlyExecutor">
          <h3>Who would you like to appoint as your Executor?</h3>
          <ValidationProvider
            id="executor_party"
            v-slot="{ errors }"
            name="executor_party"
            rules="required"
          >
            <v-radio-group v-model="executorParty" row :error-messages="errors">
              <v-radio
                v-if="showCompanyExecutor"
                :label="testator.company.tradingName"
                value="ADVISOR_COMPANY"
                color="primary"
                @click="
                  sendGADataLayer(
                    'your-people-executor-online-wills-administrator-check'
                  )
                "
              />
              <v-radio
                v-else
                :label="whitelabel.internalExecutorName"
                value="INTERNAL_COMPANY"
                color="primary"
                @click="
                  sendGADataLayer(
                    'your-people-executor-online-wills-administrator-check'
                  )
                "
              />
              <v-radio
                v-if="!executorForced"
                label="Friend or family member"
                value="FRIEND_OR_FAMILY"
                color="primary"
                @click="
                  sendGADataLayer('your-people-executor-friend-family-check')
                "
              />
            </v-radio-group>
          </ValidationProvider>

          <div v-if="executorParty == 'FRIEND_OR_FAMILY'">
            <h3>Executor:</h3>
            <p>
              Please add your Executor's contact details so the Executor can be
              contacted when you pass away.
            </p>
            <PeopleList
              :people="executorArray"
              :testator="testator"
              relation="EXECUTOR"
              :people-for-dialog="peopleForExecutorDialog"
              :limit="1"
            />
            <ValidationProvider
              id="executor"
              v-slot="{ errors }"
              name="executor"
              rules="required"
            >
              <input :value="relations.executor.id" type="hidden" />
              <span class="error-text">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="mt-6">
            <h3>Co-executor</h3>
            <p>
              You can appoint a co-executor if you wish to appoint two people to
              be the Executor of your estate, which can help with
              accountability, but can also cause delays in the administration.
            </p>
            <PeopleList
              :people="coExecutorArray"
              :testator="testator"
              relation="CO_EXECUTOR"
              :people-for-dialog="peopleForCoExecutorDialog"
              :limit="1"
            />
          </div>

          <div v-if="executorParty" class="mt-6">
            <h3>Alternative Executor:</h3>
            <p>
              If your preferred Executor is unable or declines to accept the
              task, who will you appoint as your alternative Executor?
            </p>
            <v-radio-group
              v-if="executorParty == 'FRIEND_OR_FAMILY'"
              v-model="alternativeExecutorParty"
              row
              :mandatory="false"
            >
              <v-radio
                v-if="showCompanyExecutor"
                :label="testator.company.tradingName"
                value="ADVISOR_COMPANY"
                color="primary"
              />
              <v-radio
                v-else
                :label="whitelabel.internalExecutorName"
                value="INTERNAL_COMPANY"
                color="primary"
              />
              <v-radio
                label="Friend or family member"
                value="FRIEND_OR_FAMILY"
                color="primary"
              />
            </v-radio-group>

            <div v-if="alternativeExecutorParty == 'FRIEND_OR_FAMILY'">
              <p>
                Please add your alternative Executor's contact details if you
                intend for {{ companyName }} to store your will, so we can
                contact them to send your will to them when the time comes.
              </p>
              <PeopleList
                :people="alternativeExecutorArray"
                :testator="testator"
                relation="ALTERNATIVE_EXECUTOR"
                :people-for-dialog="peopleForAlternativeExecutorDialog"
                :limit="1"
              />
            </div>
          </div>
        </ValidationObserver>

        <div class="button-container">
          <DefaultButton
            @defaultButtonClicked="redirectBack"
            :callout="'left'"
            :small="true"
          />
          <GradientButton
            :title="'Next'"
            :small="true"
            :callout="'right'"
            :loading="buttonLoading"
            @gradientButtonClicked="saveAndNext"
          />
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import { UPDATE_TESTATOR_MUTATION } from '../../constants/graphql/testators';
  import PeopleList from './PeopleList.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { getAge } from '../../constants/js/people';

  export default {
    name: 'AppointExecutor',
    components: {
      PeopleList,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
      relations: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        executorParty: this.testator.executorParty,
        alternativeExecutorParty: this.testator.alternativeExecutorParty,
        wizard: this.$route.query.peopleWizard,
      };
    },
    computed: {
      omOnlyExecutor() {
        if (this.testator.bypassOmOnlyExecutor) {
          return false;
        }
        return this.testator.enabledFeatures.includes('om_only_executor');
      },
      alternativeExecutorArray() {
        return this.relations.alternativeExecutor.id
          ? [this.relations.alternativeExecutor]
          : [];
      },
      executorArray() {
        return this.relations.executor.id ? [this.relations.executor] : [];
      },
      coExecutorArray() {
        return this.relations.coExecutor.id ? [this.relations.coExecutor] : [];
      },
      peopleForExecutorDialog() {
        return this.testator.people.map(p => {
          if (this.relations.coExecutor == p) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the co-executor',
            };
          } else if (
            this.relations.alternativeExecutor == p &&
            this.alternativeExecutorParty == 'FRIEND_OR_FAMILY'
          ) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the alternative Executor',
            };
          } else if (p.dateOfBirth && getAge(p.dateOfBirth) < 18) {
            return {
              ...p,
              disabledMessage: 'This person is younger than 18 years old',
            };
          } else {
            return p;
          }
        });
      },
      peopleForCoExecutorDialog() {
        return this.testator.people.map(p => {
          if (
            this.relations.executor == p &&
            this.executorParty == 'FRIEND_OR_FAMILY'
          ) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the Executor',
            };
          } else if (
            this.relations.alternativeExecutor == p &&
            this.alternativeExecutorParty == 'FRIEND_OR_FAMILY'
          ) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the alternative Executor',
            };
          } else if (p.dateOfBirth && getAge(p.dateOfBirth) < 18) {
            return {
              ...p,
              disabledMessage: 'This person is younger than 18 years old',
            };
          } else {
            return p;
          }
        });
      },
      peopleForAlternativeExecutorDialog() {
        return this.testator.people.map(p => {
          if (
            this.relations.executor == p &&
            this.executorParty == 'FRIEND_OR_FAMILY'
          ) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the Executor',
            };
          } else if (this.relations.coExecutor == p) {
            return {
              ...p,
              disabledMessage:
                'This person is already selected as the co-executor',
            };
          } else if (p.dateOfBirth && getAge(p.dateOfBirth) < 18) {
            return {
              ...p,
              disabledMessage: 'This person is younger than 18 years old',
            };
          } else {
            return p;
          }
        });
      },
      showCompanyExecutor() {
        return (
          this.testator.company &&
          (this.testator.company.executorship == 'COMPANY_OR_PERSON' ||
            this.testator.company.executorship == 'COMPANY_ONLY')
        );
      },
      executorForced() {
        if (this.testator.bypassOmOnlyExecutor) {
          return false;
        }
        return (
          this.testator.company &&
          (this.testator.company.executorship == 'COMPANY_ONLY' ||
            this.testator.company.executorship == 'INTERNAL_COMPANY_ONLY')
        );
      },
      companyName() {
        return this.showCompanyExecutor
          ? this.testator.company.tradingName
          : this.whitelabel.internalExecutorName;
      },
      companyNameText() {
        return this.companyName.includes('Administrators')
          ? this.companyName
          : `${this.companyName} Executors`;
      },
      country() {
        return this.testator.willCountry == 'GB'
          ? 'the United Kingdom'
          : 'South Africa';
      },
      isZAWill() {
        return this.testator.willCountry == 'ZA';
      },
      showLearnMoreAboutQWExecutorLink() {
        return (
          !this.showCompanyExecutor &&
          this.isZAWill &&
          this.whitelabel.showQWExecutorLinks
        );
      },
    },
    watch: {
      executorParty: function (newValue) {
        this.alternativeExecutorParty =
          newValue == 'INTERNAL_COMPANY' || newValue == 'ADVISOR_COMPANY'
            ? 'FRIEND_OR_FAMILY'
            : this.testator.alternativeExecutorParty;
      },
    },
    created() {
      this.alternativeExecutorParty =
        this.executorParty == 'INTERNAL_COMPANY' ||
        this.executorParty == 'ADVISOR_COMPANY'
          ? 'FRIEND_OR_FAMILY'
          : this.testator.alternativeExecutorParty;
    },
    methods: {
      sendGADataLayer(event) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: event,
          omuserid: this.$store.getters?.userId,
        });
      },
      saveAndNext() {
        if (this.omOnlyExecutor) {
          this.buttonLoading = true;
          this.updateTestator();
        } else {
          this.$refs.observer.validate().then(valid => {
            if (valid) {
              this.buttonLoading = true;
              this.updateTestator();
            } else {
              const errors = this.$refs.observer.ctx.errors;
              document
                .getElementById(
                  Object.keys(errors).find(field => errors[field].length)
                )
                .scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          });
        }
      },
      updateTestator() {
        let testatorInputDetails = {};

        if (this.omOnlyExecutor) {
          testatorInputDetails = {
            executorParty: 'INTERNAL_COMPANY',
          };
        } else {
          var altExecutorParty =
            this.alternativeExecutorParty == 'FRIEND_OR_FAMILY' &&
            !this.relations.alternativeExecutor.id
              ? null
              : this.alternativeExecutorParty;
          testatorInputDetails = {
            executorParty: this.executorParty,
            alternativeExecutorParty: altExecutorParty,
          };
        }

        const id = this.testator.id;
        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      redirectToNext() {
        if (this.wizard == 'executor') {
          this.$router.push({ name: 'PeopleOverview', hash: '#executor' });
        } else {
          this.$router.push('optional-inheritors');
        }
      },
      redirectBack() {
        if (this.wizard == 'executor') {
          this.$router.push({ name: 'PeopleOverview', hash: '#executor' });
        } else {
          this.$router.push('emergency-contacts');
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .button-container {
    margin-top: 40px;
    display: flex;
    gap: 20px;
  }
</style>
